import { InvalidCredentialsError } from "../../../domain/errors/invalid-credentials-error";
import { UnexpectedError } from "../../../domain/errors/unexpected-error";
import { EnumTypesProps, FilterModel } from "../../../domain/models/enum";
import {
  Taker,
  TakerModel,
  TakerResponseModel,
} from "../../../domain/models/taker";
import { HttpClient, HttpStatusCode } from "../../protocols/http/http-client";

export class RemoteTaker implements Taker {
  constructor(
    private readonly url: string,
    private readonly httpPostClient: HttpClient
  ) {}

  async load(params?: FilterModel): Promise<TakerResponseModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}`,
      method: "get",
      params,
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async create(body: TakerModel): Promise<TakerModel> {
    const httpResponse = await this.httpPostClient.request({
      url: this.url,
      method: "post",
      body,
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.created:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.conflict:
        throw httpResponse.body;
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async delete(id: number): Promise<TakerModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/${id}`,
      method: "delete",
    });
    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.conflict:
        throw httpResponse.body;
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async loadById(id: number): Promise<TakerModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/${id}`,
      method: "get",
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async update(id: number | undefined, body: TakerModel): Promise<TakerModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/${id}`,
      method: "put",
      body,
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async loadAll(): Promise<EnumTypesProps[]> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/all`,
      method: "get",
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async getByDeveloper(): Promise<TakerModel[]> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/developer`,
      method: "get",
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      default:
        throw new UnexpectedError();
    }
  }
}
